import { useMemo } from 'react';
import {
  AlertCircle,
  Archive,
  CalendarClock,
  ChevronRight,
  Clock,
  Loader2,
} from 'lucide-react';
import {
  useDailyStatementSchedule,
  useDailyStatements,
} from '../../../../services/httpServices/queries';
import type { DailyStatementSummary } from '../../../../types/reports';
import {
  formatDateWithWeekday,
  formatOrdinalDateTimeInTimeZone,
} from '../../../../utils/dateFormat';
import { formatCurrency } from '../reportFormat';

interface DailyStatementArchiveProps {
  /** Currently selected day in the report filters (`YYYY-MM-DD`). */
  selectedDate: string;
  onOpen: (date: string) => void;
}

const MODE_STYLES: Record<string, string> = {
  AUTO: 'bg-primary/10 text-primary',
  MANUAL: 'bg-secondary/10 text-secondary',
  LIVE: 'bg-warning/10 text-warning',
};

/**
 * Saved Daily Statement snapshots plus the automatic freeze schedule.
 *
 * The report itself can always compute a live day, so this panel answers the
 * two questions the live result cannot: which days are already frozen, and when
 * the next nightly snapshot will run.
 */
export function DailyStatementArchive({
  selectedDate,
  onOpen,
}: DailyStatementArchiveProps) {
  const scheduleQuery = useDailyStatementSchedule();
  const archiveQuery = useDailyStatements();

  const schedule = scheduleQuery.data?.data;
  const statements = useMemo(
    () => archiveQuery.data?.data ?? [],
    [archiveQuery.data],
  );

  // Newest first from the API. Show a compact recent window; the full archive is
  // still searchable by picking a date in the report filters.
  const visible = useMemo(
    () => [...statements].sort((a, b) => b.date.localeCompare(a.date)).slice(0, 8),
    [statements],
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-border bg-surface shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border bg-gradient-to-r from-primary/5 via-transparent to-transparent px-4 py-3.5 sm:px-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <Archive size={17} />
          </span>
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-text">Daily statement archive</h2>
            <p className="mt-0.5 text-xs text-text-muted">
              Frozen snapshots are kept for audit and reconcile with the Top Sheet.
              Open any day to load its saved statement.
            </p>
          </div>
        </div>
        {archiveQuery.data && (
          <span className="rounded-full border border-border bg-surface px-2.5 py-1 text-[0.6875rem] font-medium text-text-muted shadow-sm">
            {statements.length} frozen day{statements.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border bg-background/40 px-4 py-2.5 text-[0.6875rem] text-text-muted sm:px-5">
        <span className="inline-flex items-center gap-1.5">
          <CalendarClock size={13} className="text-primary" />
          {scheduleQuery.isLoading ? (
            'Loading schedule…'
          ) : schedule?.isEnabled === false ? (
            <span className="font-medium text-text">Automatic freeze disabled</span>
          ) : (
            <>
              Auto-freezes daily
              {schedule?.nextRunAt && (
                <>
                  {' · next '}
                  <span className="font-medium text-text">
                    {formatOrdinalDateTimeInTimeZone(
                      schedule.nextRunAt,
                      schedule.timezone,
                    )}
                  </span>
                </>
              )}
            </>
          )}
        </span>
        {schedule?.timezone && (
          <span className="inline-flex items-center gap-1.5">
            <Clock size={13} />
            {schedule.timezone}
          </span>
        )}
        {schedule?.pattern && (
          <span>
            Cron <code className="rounded bg-surface px-1">{schedule.pattern}</code>
          </span>
        )}
      </div>

      <div className="p-4 sm:p-5">
        {archiveQuery.isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-primary" />
          </div>
        ) : archiveQuery.error ? (
          <div className="flex items-start gap-2 rounded-lg border border-danger/20 bg-danger/10 px-3 py-2 text-sm text-danger">
            <AlertCircle size={16} className="mt-0.5 shrink-0" />
            <span>Could not load the Daily Statement archive.</span>
          </div>
        ) : statements.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-background/40 px-4 py-8 text-center">
            <Archive size={24} className="mx-auto mb-2 text-text-muted/50" />
            <p className="text-sm font-medium text-text">No frozen statements yet</p>
            <p className="mt-1 text-xs text-text-muted">
              Use <span className="font-medium text-text">Save day snapshot</span> above,
              or wait for the nightly automatic run.
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
              {visible.map((statement) => (
                <StatementCard
                  key={statement.date}
                  statement={statement}
                  active={statement.date === selectedDate}
                  timezone={schedule?.timezone}
                  onOpen={onOpen}
                />
              ))}
            </div>
            {statements.length > visible.length && (
              <p className="mt-3 text-[0.6875rem] text-text-muted">
                Showing the latest {visible.length} of {statements.length} frozen days.
                Use the period picker to open an older date.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatementCard({
  statement,
  active,
  timezone,
  onOpen,
}: {
  statement: DailyStatementSummary;
  active: boolean;
  timezone?: string;
  onOpen: (date: string) => void;
}) {
  const kpis = statement.kpis;
  return (
    <button
      type="button"
      onClick={() => onOpen(statement.date)}
      className={`group flex h-full flex-col rounded-xl border p-3 text-left transition-colors ${
        active
          ? 'border-primary/50 bg-primary/[0.06] ring-1 ring-primary/20'
          : 'border-border bg-background/40 hover:border-primary/40 hover:bg-primary/[0.03]'
      }`}
      title={`Open the frozen statement for ${statement.date}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-text">
            {formatDateWithWeekday(statement.date)}
          </p>
          <p className="mt-0.5 truncate text-[0.625rem] text-text-muted">
            {formatOrdinalDateTimeInTimeZone(statement.generatedAt, timezone)}
          </p>
        </div>
        <ChevronRight
          size={14}
          className="mt-0.5 shrink-0 text-text-muted transition-transform group-hover:translate-x-0.5 group-hover:text-primary"
        />
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        <span
          className={`rounded-full px-1.5 py-0.5 text-[0.5625rem] font-semibold uppercase tracking-wide ${
            MODE_STYLES[statement.generatedMode] || 'bg-text-muted/10 text-text-muted'
          }`}
        >
          {statement.generatedMode}
        </span>
        <span className="rounded-full bg-surface px-1.5 py-0.5 text-[0.5625rem] font-medium text-text-muted ring-1 ring-border">
          v{statement.version}
        </span>
      </div>

      {kpis && (
        <dl className="mt-2.5 grid grid-cols-2 gap-x-2 gap-y-1 border-t border-border/70 pt-2 text-[0.625rem]">
          <div>
            <dt className="text-text-muted">Revenue</dt>
            <dd className="font-semibold tabular-nums text-text">
              {formatCurrency(kpis.revenue)}
            </dd>
          </div>
          <div>
            <dt className="text-text-muted">Collected</dt>
            <dd className="font-semibold tabular-nums text-success">
              {formatCurrency(kpis.collected)}
            </dd>
          </div>
        </dl>
      )}
    </button>
  );
}
